<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

interface SceneChoiceOption {
  value: string
  label: string
  detail: string
}

interface SceneGradientOption {
  value: string
  label: string
}

type GradientMenuKind = 'elevation' | 'intensity' | 'gps'

withDefaults(
  defineProps<{
    title?: string
    subtitle?: string
    badge: string
    shapeOptions: SceneChoiceOption[]
    shapeValue: string
    shapeLabel: string
    sizingOptions: SceneChoiceOption[]
    sizingValue: string
    sizingLabel: string
    showFixedPointSize: boolean
    pointSize: number
    renderOptions: SceneChoiceOption[]
    renderValue: string
    renderLabel: string
    gradientOptions: SceneGradientOption[]
    showElevationControls: boolean
    elevationGradientValue: string
    elevationGradientLabel: string
    elevationGradientCss: string
    elevationBounds: [number, number]
    elevationRange: [number, number]
    elevationStep: number
    showIntensityControls: boolean
    intensityGradientValue: string
    intensityGradientLabel: string
    intensityGradientCss: string
    intensityBounds: [number, number]
    intensityRange: [number, number]
    intensityStep: number
    showGpsControls: boolean
    gpsGradientValue: string
    gpsGradientLabel: string
    gpsGradientCss: string
    gpsBounds: [number, number]
    gpsRange: [number, number]
    gpsStep: number
    showSingleColorControls: boolean
    singleColor: string
    singleColorSwatches: string[]
    useEDL: boolean
    opacity: number
  }>(),
  {
    title: '点云属性',
    subtitle: '点大小、渲染模式、EDL 与透明度',
  },
)

const emit = defineEmits<{
  (event: 'select-shape', value: string): void
  (event: 'select-sizing', value: string): void
  (event: 'update-point-size', value: number): void
  (event: 'select-render', value: string): void
  (event: 'select-elevation-gradient', value: string): void
  (event: 'update-elevation-min', value: number): void
  (event: 'update-elevation-max', value: number): void
  (event: 'select-intensity-gradient', value: string): void
  (event: 'update-intensity-min', value: number): void
  (event: 'update-intensity-max', value: number): void
  (event: 'select-gps-gradient', value: string): void
  (event: 'update-gps-min', value: number): void
  (event: 'update-gps-max', value: number): void
  (event: 'update-single-color', value: string): void
  (event: 'toggle-edl'): void
  (event: 'update-opacity', value: number): void
}>()

const gradientMenuRef = ref<HTMLElement | null>(null)
const openMenu = ref<GradientMenuKind | null>(null)

function toggleGradientMenu(kind: GradientMenuKind) {
  openMenu.value = openMenu.value === kind ? null : kind
}

function isMenuOpen(kind: GradientMenuKind) {
  return openMenu.value === kind
}

function closeGradientMenu() {
  openMenu.value = null
}

function handleDocumentPointerDown(event: PointerEvent) {
  const target = event.target

  if (!(target instanceof Node)) {
    return
  }

  if (!gradientMenuRef.value?.contains(target)) {
    closeGradientMenu()
  }
}

function selectGradient(kind: GradientMenuKind, value: string) {
  if (kind === 'elevation') {
    emit('select-elevation-gradient', value)
  } else if (kind === 'intensity') {
    emit('select-intensity-gradient', value)
  } else {
    emit('select-gps-gradient', value)
  }

  closeGradientMenu()
}

function formatScalar(value: number): string {
  if (!Number.isFinite(value)) {
    return '--'
  }

  return value.toFixed(2)
}

function normalizeBounds(bounds: [number, number]): [number, number] {
  let [min, max] = bounds

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return [0, 1]
  }

  if (min > max) {
    ;[min, max] = [max, min]
  }

  if (min === max) {
    max = min + 1
  }

  return [min, max]
}

function getRangeSelectionStyle(
  range: [number, number],
  bounds: [number, number],
) {
  const [boundMin, boundMax] = normalizeBounds(bounds)
  const span = boundMax - boundMin

  if (!Number.isFinite(span) || span <= 0) {
    return { left: '0%', width: '100%' }
  }

  const left = ((range[0] - boundMin) / span) * 100
  const right = ((range[1] - boundMin) / span) * 100
  const clampedLeft = Math.max(0, Math.min(100, left))
  const clampedRight = Math.max(0, Math.min(100, right))

  return {
    left: `${clampedLeft}%`,
    width: `${Math.max(0, clampedRight - clampedLeft)}%`,
  }
}

onMounted(() => {
  document.addEventListener('pointerdown', handleDocumentPointerDown)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleDocumentPointerDown)
})
</script>

<template>
  <details class="panel-section" open>
    <summary class="panel-section-summary">
      <span class="summary-main">
        <span class="panel-section-title">{{ title }}</span>
        <span class="panel-section-subtitle">{{ subtitle }}</span>
      </span>
      <span class="summary-side">
        <span class="summary-count">{{ badge }}</span>
        <span class="summary-chevron" aria-hidden="true">›</span>
      </span>
    </summary>

    <div class="panel-section-body stacked-panel-body">
      <details class="panel-subsection" open>
        <summary class="panel-subsection-summary">
          <span class="summary-main">
            <span class="panel-subsection-title">点形状</span>
            <span class="panel-section-subtitle">方形与圆形切换</span>
          </span>
          <span class="summary-side">
            <span class="card-badge">{{ shapeLabel }}</span>
            <span class="summary-chevron" aria-hidden="true">›</span>
          </span>
        </summary>

        <div class="panel-subsection-body">
          <div class="button-grid">
            <button
              v-for="item in shapeOptions"
              :key="item.value"
              class="panel-button"
              :class="{ active: shapeValue === item.value }"
              @click="emit('select-shape', item.value)"
            >
              <span class="panel-button-label">{{ item.label }}</span>
              <span class="panel-button-meta">{{ item.detail }}</span>
            </button>
          </div>
        </div>
      </details>

      <details class="panel-subsection" open>
        <summary class="panel-subsection-summary">
          <span class="summary-main">
            <span class="panel-subsection-title">点类型</span>
            <span class="panel-section-subtitle">自适应与固定点大小切换</span>
          </span>
          <span class="summary-side">
            <span class="card-badge">{{ sizingLabel }}</span>
            <span class="summary-chevron" aria-hidden="true">›</span>
          </span>
        </summary>

        <div class="panel-subsection-body">
          <div class="button-grid">
            <button
              v-for="item in sizingOptions"
              :key="item.value"
              class="panel-button"
              :class="{ active: sizingValue === item.value }"
              @click="emit('select-sizing', item.value)"
            >
              <span class="panel-button-label">{{ item.label }}</span>
              <span class="panel-button-meta">{{ item.detail }}</span>
            </button>
          </div>

          <div v-if="showFixedPointSize" class="slider-card">
            <div class="slider-head">
              <span class="slider-label">点大小</span>
              <strong class="slider-value">{{ pointSize.toFixed(2) }}</strong>
            </div>
            <input
              class="panel-range"
              type="range"
              min="0.5"
              max="10"
              step="0.1"
              :value="pointSize"
              @input="emit('update-point-size', Number(($event.target as HTMLInputElement).value))"
            />
          </div>
        </div>
      </details>

      <details class="panel-subsection" open>
        <summary class="panel-subsection-summary">
          <span class="summary-main">
            <span class="panel-subsection-title">渲染模式</span>
            <span class="panel-section-subtitle">颜色模式、EDL 与透明度控制</span>
          </span>
          <span class="summary-side">
            <span class="card-badge">{{ renderLabel }}</span>
            <span class="summary-chevron" aria-hidden="true">›</span>
          </span>
        </summary>

        <div class="panel-subsection-body">
          <div class="button-grid button-grid-three">
            <button
              v-for="item in renderOptions"
              :key="item.value"
              class="panel-button"
              :class="{ active: renderValue === item.value }"
              @click="emit('select-render', item.value)"
            >
              <span class="panel-button-label">{{ item.label }}</span>
              <span class="panel-button-meta">{{ item.detail }}</span>
            </button>
          </div>

          <div v-if="showElevationControls" class="mode-controls">
            <div class="slider-card">
              <div class="slider-head">
                <span class="slider-label">色带</span>
                <strong class="slider-value">{{ elevationGradientLabel }}</strong>
              </div>

              <div ref="gradientMenuRef" class="custom-select" :class="{ open: isMenuOpen('elevation') }">
                <button type="button" class="custom-select-trigger" @click="toggleGradientMenu('elevation')">
                  <span>{{ elevationGradientLabel }}</span>
                  <span class="custom-select-chevron" aria-hidden="true">▾</span>
                </button>

                <div v-if="isMenuOpen('elevation')" class="custom-select-menu">
                  <button
                    v-for="item in gradientOptions"
                    :key="item.value"
                    type="button"
                    class="custom-select-option"
                    :class="{ active: elevationGradientValue === item.value }"
                    @click="selectGradient('elevation', item.value)"
                  >
                    {{ item.label }}
                  </button>
                </div>
              </div>

              <div class="gradient-preview" :style="{ background: elevationGradientCss }"></div>
            </div>

            <div class="slider-card elevation-range-card">
              <div class="slider-head">
                <span class="slider-label">高程区间</span>
                <strong class="slider-value">
                  {{ formatScalar(elevationRange[0]) }} - {{ formatScalar(elevationRange[1]) }}
                </strong>
              </div>

              <div class="range-meta">
                <span>最低 {{ formatScalar(elevationBounds[0]) }}</span>
                <span>最高 {{ formatScalar(elevationBounds[1]) }}</span>
              </div>

              <div class="range-shell">
                <div class="gradient-track" :style="{ background: elevationGradientCss }">
                  <div class="gradient-track-window" :style="getRangeSelectionStyle(elevationRange, elevationBounds)"></div>
                </div>

                <input
                  class="panel-range dual-range"
                  type="range"
                  :min="elevationBounds[0]"
                  :max="elevationBounds[1]"
                  :step="elevationStep"
                  :value="elevationRange[0]"
                  @input="emit('update-elevation-min', Number(($event.target as HTMLInputElement).value))"
                />
                <input
                  class="panel-range dual-range"
                  type="range"
                  :min="elevationBounds[0]"
                  :max="elevationBounds[1]"
                  :step="elevationStep"
                  :value="elevationRange[1]"
                  @input="emit('update-elevation-max', Number(($event.target as HTMLInputElement).value))"
                />
              </div>
            </div>
          </div>

          <div v-if="showIntensityControls" class="mode-controls">
            <div class="slider-card">
              <div class="slider-head">
                <span class="slider-label">强度色带</span>
                <strong class="slider-value">{{ intensityGradientLabel }}</strong>
              </div>

              <div ref="gradientMenuRef" class="custom-select" :class="{ open: isMenuOpen('intensity') }">
                <button type="button" class="custom-select-trigger" @click="toggleGradientMenu('intensity')">
                  <span>{{ intensityGradientLabel }}</span>
                  <span class="custom-select-chevron" aria-hidden="true">▾</span>
                </button>

                <div v-if="isMenuOpen('intensity')" class="custom-select-menu">
                  <button
                    v-for="item in gradientOptions"
                    :key="item.value"
                    type="button"
                    class="custom-select-option"
                    :class="{ active: intensityGradientValue === item.value }"
                    @click="selectGradient('intensity', item.value)"
                  >
                    {{ item.label }}
                  </button>
                </div>
              </div>

              <div class="gradient-preview" :style="{ background: intensityGradientCss }"></div>
            </div>

            <div class="slider-card elevation-range-card">
              <div class="slider-head">
                <span class="slider-label">强度区间</span>
                <strong class="slider-value">
                  {{ formatScalar(intensityRange[0]) }} - {{ formatScalar(intensityRange[1]) }}
                </strong>
              </div>

              <div class="range-meta">
                <span>最低 {{ formatScalar(intensityBounds[0]) }}</span>
                <span>最高 {{ formatScalar(intensityBounds[1]) }}</span>
              </div>

              <div class="range-shell">
                <div class="gradient-track" :style="{ background: intensityGradientCss }">
                  <div class="gradient-track-window" :style="getRangeSelectionStyle(intensityRange, intensityBounds)"></div>
                </div>

                <input
                  class="panel-range dual-range"
                  type="range"
                  :min="intensityBounds[0]"
                  :max="intensityBounds[1]"
                  :step="intensityStep"
                  :value="intensityRange[0]"
                  @input="emit('update-intensity-min', Number(($event.target as HTMLInputElement).value))"
                />
                <input
                  class="panel-range dual-range"
                  type="range"
                  :min="intensityBounds[0]"
                  :max="intensityBounds[1]"
                  :step="intensityStep"
                  :value="intensityRange[1]"
                  @input="emit('update-intensity-max', Number(($event.target as HTMLInputElement).value))"
                />
              </div>
            </div>
          </div>

          <div v-if="showGpsControls" class="mode-controls">
            <div class="slider-card">
              <div class="slider-head">
                <span class="slider-label">时间色带</span>
                <strong class="slider-value">{{ gpsGradientLabel }}</strong>
              </div>

              <div ref="gradientMenuRef" class="custom-select" :class="{ open: isMenuOpen('gps') }">
                <button type="button" class="custom-select-trigger" @click="toggleGradientMenu('gps')">
                  <span>{{ gpsGradientLabel }}</span>
                  <span class="custom-select-chevron" aria-hidden="true">▾</span>
                </button>

                <div v-if="isMenuOpen('gps')" class="custom-select-menu">
                  <button
                    v-for="item in gradientOptions"
                    :key="item.value"
                    type="button"
                    class="custom-select-option"
                    :class="{ active: gpsGradientValue === item.value }"
                    @click="selectGradient('gps', item.value)"
                  >
                    {{ item.label }}
                  </button>
                </div>
              </div>

              <div class="gradient-preview" :style="{ background: gpsGradientCss }"></div>
            </div>

            <div class="slider-card elevation-range-card">
              <div class="slider-head">
                <span class="slider-label">时间区间</span>
                <strong class="slider-value">
                  {{ formatScalar(gpsRange[0]) }} - {{ formatScalar(gpsRange[1]) }}
                </strong>
              </div>

              <div class="range-meta">
                <span>起始 {{ formatScalar(gpsBounds[0]) }}</span>
                <span>结束 {{ formatScalar(gpsBounds[1]) }}</span>
              </div>

              <div class="range-shell">
                <div class="gradient-track" :style="{ background: gpsGradientCss }">
                  <div class="gradient-track-window" :style="getRangeSelectionStyle(gpsRange, gpsBounds)"></div>
                </div>

                <input
                  class="panel-range dual-range"
                  type="range"
                  :min="gpsBounds[0]"
                  :max="gpsBounds[1]"
                  :step="gpsStep"
                  :value="gpsRange[0]"
                  @input="emit('update-gps-min', Number(($event.target as HTMLInputElement).value))"
                />
                <input
                  class="panel-range dual-range"
                  type="range"
                  :min="gpsBounds[0]"
                  :max="gpsBounds[1]"
                  :step="gpsStep"
                  :value="gpsRange[1]"
                  @input="emit('update-gps-max', Number(($event.target as HTMLInputElement).value))"
                />
              </div>
            </div>
          </div>

          <div v-if="showSingleColorControls" class="mode-controls">
            <div class="slider-card">
              <div class="slider-head">
                <span class="slider-label">单色</span>
                <strong class="slider-value">{{ singleColor }}</strong>
              </div>

              <label class="color-picker-shell">
                <input
                  class="color-picker-input"
                  type="color"
                  :value="singleColor"
                  @input="emit('update-single-color', ($event.target as HTMLInputElement).value)"
                />
                <span class="color-picker-value">{{ singleColor }}</span>
              </label>

              <div class="swatch-grid">
                <button
                  v-for="color in singleColorSwatches"
                  :key="color"
                  type="button"
                  class="swatch-button"
                  :class="{ active: singleColor.toLowerCase() === color.toLowerCase() }"
                  :style="{ background: color }"
                  @click="emit('update-single-color', color)"
                ></button>
              </div>
            </div>
          </div>

          <button class="panel-button panel-button-wide panel-toggle" :class="{ active: useEDL }" @click="emit('toggle-edl')">
            <span>
              <span class="panel-button-label">EDL</span>
              <span class="panel-button-meta">增强边缘层次和空间纵深</span>
            </span>
            <span class="toggle-indicator">{{ useEDL ? '开' : '关' }}</span>
          </button>

          <div class="slider-card">
            <div class="slider-head">
              <span class="slider-label">不透明度</span>
              <strong class="slider-value">{{ opacity.toFixed(2) }}</strong>
            </div>
            <input
              class="panel-range"
              type="range"
              min="0"
              max="1"
              step="0.05"
              :value="opacity"
              @input="emit('update-opacity', Number(($event.target as HTMLInputElement).value))"
            />
          </div>
        </div>
      </details>
    </div>
  </details>
</template>

<style scoped>
.mode-controls,
.panel-toggle {
  margin-top: 14px;
}

.slider-card {
  margin-top: 14px;
  padding: 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.slider-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.slider-label {
  font-size: 13px;
  color: #c9d9e4;
}

.slider-value,
.toggle-indicator {
  font-size: 12px;
  color: #eff9ff;
}

.panel-range {
  width: 100%;
  margin-top: 12px;
  accent-color: #6fc4b6;
}

.panel-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.custom-select {
  position: relative;
  margin-top: 12px;
}

.custom-select-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid rgba(145, 182, 204, 0.2);
  border-radius: 12px;
  background:
    linear-gradient(180deg, rgba(18, 24, 32, 0.96) 0%, rgba(12, 16, 22, 0.96) 100%);
  color: #eef7ff;
  padding: 10px 12px;
  font-size: 13px;
  cursor: pointer;
}

.custom-select.open .custom-select-trigger {
  border-color: rgba(103, 185, 255, 0.4);
  box-shadow: 0 0 0 1px rgba(103, 185, 255, 0.12);
}

.custom-select-chevron {
  color: #9ec5da;
  transition: transform 140ms ease;
}

.custom-select.open .custom-select-chevron {
  transform: rotate(180deg);
}

.custom-select-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 20;
  display: grid;
  gap: 4px;
  padding: 8px;
  border: 1px solid rgba(145, 182, 204, 0.18);
  border-radius: 14px;
  background: rgba(12, 16, 22, 0.98);
  box-shadow: 0 18px 34px rgba(0, 0, 0, 0.34);
}

.custom-select-option {
  width: 100%;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #e8f5ff;
  padding: 8px 10px;
  text-align: left;
  font-size: 13px;
  cursor: pointer;
}

.custom-select-option:hover {
  background: rgba(255, 255, 255, 0.06);
}

.custom-select-option.active {
  background: rgba(71, 135, 255, 0.84);
  color: #ffffff;
}

.gradient-preview {
  height: 16px;
  margin-top: 12px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06);
}

.elevation-range-card {
  overflow: hidden;
}

.range-meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
  font-size: 12px;
  color: #9eb7c8;
}

.range-shell {
  position: relative;
  height: 34px;
  margin-top: 12px;
}

.gradient-track {
  position: absolute;
  top: 9px;
  left: 0;
  right: 0;
  height: 16px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  opacity: 0.42;
}

.gradient-track-window {
  position: absolute;
  top: -1px;
  bottom: -1px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.9);
  box-shadow:
    0 0 0 1px rgba(14, 19, 25, 0.6),
    0 8px 18px rgba(0, 0, 0, 0.28);
}

.dual-range {
  position: absolute;
  inset: 0;
  margin: 0;
  background: transparent;
  pointer-events: none;
  -webkit-appearance: none;
  appearance: none;
}

.dual-range::-webkit-slider-runnable-track {
  height: 34px;
  background: transparent;
}

.dual-range::-moz-range-track {
  height: 34px;
  background: transparent;
}

.dual-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  margin-top: 8px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.95);
  background: #112531;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.34);
  pointer-events: auto;
  cursor: pointer;
}

.dual-range::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.95);
  background: #112531;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.34);
  pointer-events: auto;
  cursor: pointer;
}

.color-picker-shell {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(145, 182, 204, 0.2);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
}

.color-picker-input {
  width: 44px;
  height: 32px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.color-picker-value {
  font-size: 13px;
  color: #eef7ff;
}

.swatch-grid {
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: 8px;
  margin-top: 12px;
}

.swatch-button {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.14);
  cursor: pointer;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.18);
}

.swatch-button.active {
  border-color: rgba(255, 255, 255, 0.92);
  box-shadow:
    0 0 0 2px rgba(103, 185, 255, 0.2),
    inset 0 0 0 1px rgba(0, 0, 0, 0.22);
}
</style>
